import { TOKEN } from 'features/auth/constants';
import {
  getAuthToken,
  handleAuthFailure,
  saveRedirectUrl,
} from 'features/auth/helpers/helpers';
import { shouldEndSessionOnCurrentUserError } from 'features/user/helpers';
import { useGetCurrentUserQuery } from 'features/user/query';
import { memo, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getPath } from 'routes/router-paths';
import { type TPrivateRouteProps } from 'routes/types';

/**
 * Route Guard component protecting all private routes.
 *
 * Uses the `refresh_token` cookie as the indicator for an active authenticated session:
 * - Has `refresh_token`: Grants access to the route and lazy-fetches the current user profile.
 * - Missing `refresh_token`: Saves the current destination URL and redirects to `/auth/login`.
 * - `GET /user/me` fails (timeout, 404, 5xx): Ends the session and redirects to `/auth/login`.
 * The authentication flow sets access/refresh tokens in browser cookies.
 *
 * @param props - Component props containing children elements and optional route info.
 * @returns The children elements if authenticated, otherwise a Navigate redirect element.
 */
export const PrivateRoute = memo(({ children }: TPrivateRouteProps) => {
  const isAuthenticated = Boolean(getAuthToken(TOKEN.REFRESH_TOKEN));
  const location = useLocation();

  const { pathname, search, hash } = location;

  // Skip when unauthenticated to prevent triggering the query after logout
  // (tokens are cleared, but component might still be mounted or remounting)
  const { isError, error } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthenticated,
  });

  // `/me` is the root request: a timeout (408), 404 or 5xx kicks the user to the login page instead of
  // rendering an empty profile. 401 (refresh token) and 403 stay with the axios interceptor.
  const shouldEndSession =
    isAuthenticated && isError && shouldEndSessionOnCurrentUserError(error);

  useEffect(() => {
    if (shouldEndSession) {
      handleAuthFailure();
    }
  }, [shouldEndSession]);

  if (shouldEndSession) {
    return null;
  }

  if (!isAuthenticated) {
    // Preserve current URL to redirect back after logging in
    saveRedirectUrl(`${pathname}${search}${hash}`);

    return <Navigate to={getPath('loginPage')} replace />;
  }

  return children;
});
